import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Row,
  Col,
  Spinner,
  Container,
} from "reactstrap";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import DOMPurify from "dompurify";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import {
  fetchLatestTerms,
  createOrUpdateTermsItem,
  updateTermsItem,
  deleteTermsItem,
} from "../../slices/thunks";

const TermsPage = () => {
  const dispatch = useDispatch();
  const { terms, loading } = useSelector((state) => state.policyTerms);

  const [contentHtml, setContentHtml] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("en");

  // Load terms when language changes
  useEffect(() => {
    dispatch(fetchLatestTerms({ lang: selectedLanguage }));
  }, [dispatch, selectedLanguage]);

  // Sync editor content when terms change
  useEffect(() => {
    if (terms?.contentHtml) {
      setContentHtml(terms.contentHtml);
    } else {
      setContentHtml("");
    }
  }, [terms]);

  const handleSaveOrUpdate = () => {
    if (!contentHtml?.trim()) return;

    if (isEditing && terms?._id) {
      dispatch(updateTermsItem(terms._id, { contentHtml, language: selectedLanguage })).then(() =>
        dispatch(fetchLatestTerms({ lang: selectedLanguage }))
      );
    } else {
      dispatch(createOrUpdateTermsItem({ contentHtml, language: selectedLanguage })).then(() =>
        dispatch(fetchLatestTerms({ lang: selectedLanguage }))
      );
    }

    setIsEditing(false);
  };

  const handleDelete = () => {
    if (!terms?._id) return;
    dispatch(deleteTermsItem(terms._id)).then(() =>
      dispatch(fetchLatestTerms({ lang: selectedLanguage }))
    );
    setContentHtml("");
    setIsEditing(false);
  };

  const handlePreviewClick = () => {
    if (terms?.contentHtml) {
      setContentHtml(terms.contentHtml);
      setIsEditing(true);
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          {/* ✅ Velzon-style BreadCrumb */}
          <BreadCrumb title="Terms & Conditions" pageTitle="Terms & Conditions" />
          <Card style={{marginTop: "35px", marginBottom: "3.5rem"}}>
            <CardHeader className="d-flex justify-content-between align-items-center">
              <h5 className="card-title mb-0">Terms & Conditions</h5>
              <div style={{ width: "200px" }}>
                <select
                  className="form-select"
                  value={selectedLanguage}
                  onChange={(e) => {
                    setSelectedLanguage(e.target.value);
                    setIsEditing(false);
                  }}
                >
                  <option value="en">English 🇬🇧</option>
                  <option value="fr">Français 🇫🇷</option>
                  <option value="es">Español 🇪🇸</option>
                  <option value="hi">Kreyòl Ayisyen 🇭🇹</option>
                  <option value="pt">Português 🇵🇹</option>
                </select>
              </div>
            </CardHeader>
            <CardBody>
               {/* Preview Section */}
               <h6 className="mb-3">Preview</h6>
              <div
                className={`position-relative border rounded p-3 ${isEditing ? "border-primary shadow-sm" : ""
                  }`}
                style={{ cursor: "pointer" }}
                onClick={handlePreviewClick}
              >
                {isEditing && terms?._id && (
                  <Button
                    color="danger"
                    size="sm"
                    className="position-absolute top-0 end-0 m-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete();
                    }}
                  >
                    Delete
                  </Button>
                )}

                <div
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                      contentHtml ||
                      terms?.contentHtml ||
                      "<p>No content available</p>"
                    ),
                  }}
                />

                <div className="text-muted mt-2">
                  <small>
                    Updated:{" "}
                    {terms?.updatedAt
                      ? new Date(terms.updatedAt).toLocaleString()
                      : "-"}
                  </small>
                </div>
              </div>

              <hr />
              {/* Editor Section */}
              <div className="border rounded p-2 position-relative">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0">
                    {isEditing
                      ? "Edit Terms & Conditions"
                      : "Create New Terms & Conditions"}
                  </h6>
                  <Button
                    color={isEditing ? "warning" : "primary"}
                    onClick={handleSaveOrUpdate}
                    disabled={loading}
                  >
                    {isEditing ? "Update" : "Save"}
                  </Button>
                </div>

                <CKEditor
                  editor={ClassicEditor}
                  data={contentHtml}
                  onChange={(event, editor) => {
                    const data = editor.getData();
                    setContentHtml(data);
                  }}
                />
              </div>
            </CardBody>
          </Card>
        </Container>
      </div >
    </React.Fragment >
  );
};

export default TermsPage;
